import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '../utils/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Instagram, Facebook, Linkedin, Twitter, Music, Link as LinkIcon, Trash2, CheckCircle, Loader2 } from 'lucide-react';
import { AppNavbar } from '../components/AppNavbar';

export const SocialAccountsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);

  // Check for success/error from OAuth callback
  useEffect(() => {
    const success = searchParams.get('success');
    const error = searchParams.get('error');
    const platform = searchParams.get('platform');
    
    if (success === 'true' && platform) {
      toast.success(`${platform.charAt(0).toUpperCase() + platform.slice(1)} account connected successfully!`);
      // Clear URL params
      navigate('/social-accounts', { replace: true });
    } else if (error) {
      toast.error(`Connection failed: ${error}`);
      navigate('/social-accounts', { replace: true });
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await apiClient.get('/social/accounts');
      setAccounts(response.data.accounts || []);
    } catch (error) {
      console.error('Failed to load social accounts:', error);
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (platform) => {
    setConnecting(platform);
    try {
      const originUrl = window.location.origin;
      const response = await apiClient.get(`/social/login/${platform}`, {
        params: { origin_url: originUrl }
      });
      
      // Redirect to OAuth URL
      if (response.data.auth_url) {
        window.location.href = response.data.auth_url;
      }
    } catch (error) {
      toast.error('Failed to initiate connection. Please try again.');
      setConnecting(null);
    }
  };

  const handleDisconnect = async (accountId, accountName) => {
    if (!window.confirm(`Are you sure you want to disconnect ${accountName}?`)) return;
    
    try {
      await apiClient.delete(`/social/accounts/${accountId}`);
      toast.success('Account disconnected');
      fetchAccounts();
    } catch (error) {
      toast.error('Failed to disconnect account');
    }
  };

  const getPlatformIcon = (platform) => {
    switch(platform) {
      case 'instagram': return <Instagram className="h-5 w-5" />;
      case 'facebook': return <Facebook className="h-5 w-5" />;
      case 'linkedin': return <Linkedin className="h-5 w-5" />;
      case 'twitter': return <Twitter className="h-5 w-5" />;
      case 'tiktok': return <Music className="h-5 w-5" />;
      default: return <LinkIcon className="h-5 w-5" />;
    }
  };

  const platforms = [
    { id: 'facebook', name: 'Facebook', color: 'bg-blue-600', enabled: true },
    { id: 'instagram', name: 'Instagram', color: 'bg-gradient-to-r from-purple-500 to-pink-500', enabled: true },
    { id: 'tiktok', name: 'TikTok', color: 'bg-black', enabled: false },
    { id: 'linkedin', name: 'LinkedIn', color: 'bg-blue-700', enabled: false },
    { id: 'twitter', name: 'Twitter/X', color: 'bg-sky-500', enabled: false },
  ];

  const getConnectedAccounts = (platformId) => {
    return accounts.filter(acc => acc.platform === platformId);
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="noise-texture" />

      <AppNavbar />

      {/* Content */}
      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="social-accounts-title">Connected Accounts</h2>
          <p className="text-muted-foreground">Connect your social media accounts to post directly from RealProp</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <>
            {/* Connected Accounts */}
            {accounts.length > 0 && (
              <div className="mb-8">
                <h3 className="font-semibold mb-4 text-lg">Your Connected Accounts</h3>
                <div className="space-y-3">
                  {accounts.map((account) => (
                    <div
                      key={account.account_id}
                      className="flex items-center justify-between p-4 border border-border/50 bg-card rounded-lg"
                      data-testid={`connected-account-${account.account_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`${account.platform === 'instagram' ? 'bg-gradient-to-r from-purple-500 to-pink-500' : 'bg-blue-600'} text-white p-2 rounded-lg`}>
                          {getPlatformIcon(account.platform)}
                        </div>
                        <div>
                          <p className="font-medium">{account.account_name}</p>
                          <p className="text-sm text-muted-foreground capitalize">{account.platform}{(() => { const d = account.connected_at ? new Date(account.connected_at) : null; return d && !isNaN(d.getTime()) ? ` • Connected ${d.toLocaleDateString()}` : ' • Connected'; })()}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <CheckCircle className="h-5 w-5 text-green-500" />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDisconnect(account.account_id, account.account_name)}
                          data-testid={`disconnect-${account.account_id}`}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Platform Cards */}
            <h3 className="font-semibold mb-4 text-lg">Connect New Accounts</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="platforms-grid">
              {platforms.map((platform) => {
                const connectedAccounts = getConnectedAccounts(platform.id);
                const isConnecting = connecting === platform.id;
                
                return (
                  <div
                    key={platform.id}
                    className={`border border-border/50 bg-card rounded-lg p-6 ${!platform.enabled ? 'opacity-60' : ''}`}
                    data-testid={`platform-card-${platform.id}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`${platform.color} text-white p-3 rounded-lg`}>
                          {getPlatformIcon(platform.id)}
                        </div>
                        <div>
                          <h3 className="font-semibold">{platform.name}</h3>
                          {connectedAccounts.length > 0 && (
                            <p className="text-sm text-green-600">{connectedAccounts.length} account(s) connected</p>
                          )}
                        </div>
                      </div>
                      {connectedAccounts.length > 0 && (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      )}
                    </div>

                    {platform.enabled ? (
                      <Button
                        variant={connectedAccounts.length > 0 ? "outline" : "default"}
                        className="w-full"
                        onClick={() => handleConnect(platform.id)}
                        disabled={isConnecting}
                        data-testid={`connect-${platform.id}`}
                      >
                        {isConnecting ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Connecting...
                          </>
                        ) : (
                          connectedAccounts.length > 0 ? `Add Another ${platform.name} Account` : `Connect ${platform.name}`
                        )}
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        className="w-full"
                        disabled
                        data-testid={`connect-${platform.id}`}
                      >
                        Coming Soon
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Info Section */}
        <div className="mt-8 p-6 border border-border/50 bg-card rounded-lg">
          <h3 className="font-semibold mb-3">How it works</h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li>• Connect your social media accounts to enable direct posting</li>
            <li>• For Facebook, you'll be able to post to any Pages you manage</li>
            <li>• For Instagram, connect your Instagram Business or Creator account linked to a Facebook Page</li>
            <li>• Schedule posts or publish immediately from the campaign editor</li>
            <li>• You can disconnect accounts anytime without affecting your existing posts</li>
          </ul>
        </div>

        {/* Requirements Note */}
        <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-800">
            <strong>Instagram Requirements:</strong> To post to Instagram, you need an Instagram Business or Creator account that is connected to a Facebook Page. Personal Instagram accounts cannot be used for API posting.
          </p>
        </div>
      </main>
    </div>
  );
};
